import { Link } from '@tanstack/react-router';
import { Avatar, AvatarFallback } from '@ui/avatar.tsx';
import { Button } from '@ui/button.tsx';
import { ArrowLeftIcon, CircleDot, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type CompanyProfileHeaderProps = {
  company: {
    name: string;
    status: string;
    uniqueCode: string;
    typology?: { name?: string } | null;
    lastStepReached?: { name?: string } | null;
  };
  isCompanyBlocked: boolean;
  isCompanyRegistered: boolean;
  canAddPoints: boolean;
  isStepWorkspace?: boolean;
  showTypology?: boolean;
};

export function CompanyProfileHeader({
  company,
  isCompanyBlocked,
  isCompanyRegistered,
  canAddPoints,
  isStepWorkspace,
  showTypology
}: CompanyProfileHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <div className="space-y-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex gap-4 items-center">
            <Avatar size="sm" className="border border-border">
              <AvatarFallback text={company.name} initials={true} />
            </Avatar>

            <div className="space-y-2">
              <h1 className="text-4xl font-bold">{company.name}</h1>
              <div className="flex flex-wrap gap-2 items-center">
                <span
                  className={`inline-flex gap-1 items-center px-2 py-1 text-xs font-medium rounded-md ${
                    isCompanyBlocked
                      ? 'text-red-600 bg-red-100'
                      : isCompanyRegistered
                        ? 'text-emerald-600 bg-emerald-200'
                        : 'text-blue-600 bg-blue-100'
                  }`}
                >
                  <CircleDot size={14} />
                  <span>{t(company.status)}</span>
                </span>

                {/* Typologie affichée uniquement si le workspace a au moins une typologie isDefault = false */}
                {showTypology && company.typology?.name ? (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md">
                    {company.typology.name}
                  </span>
                ) : null}

                {/* Palier actuel — affiché uniquement si le programme utilise une logique à palier */}
                {isStepWorkspace && company.lastStepReached?.name ? (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-100 rounded-md">
                    {company.lastStepReached.name}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <Link
            to="/companies"
            className="inline-flex gap-2 items-center text-sm font-medium text-primary hover:underline"
          >
            <Button rounded="full">
              <ArrowLeftIcon />
              {t('common.back')}
            </Button>
          </Link>

          <Link
            to="/users/import"
            search={(prev) => ({
              ...prev,
              openCreateContact: true,
              companyUniqueCode: company.uniqueCode,
              companyName: company.name
            })}
          >
            <Button variant="outline" rounded="full">
              <Plus />
              {t('common.add.contact')}
            </Button>
          </Link>

          {canAddPoints ? (
            <Link
              to="/points/import"
              search={(prev) => ({
                ...prev,
                openAddPoint: true,
                companyUniqueCode: company.uniqueCode,
                companyName: company.name
              })}
            >
              <Button variant="outline" rounded="full">
                <Plus />
                {t('common.add.point')}
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
